import React, { useState, useEffect, useCallback } from "react";
import { fetchAllOperators, updateOperatorHourlyRate } from "../api";
import {
  Box,
  Typography,
  OutlinedInput,
  CircularProgress,
  Alert,
  Card,
  Stack,
  Snackbar,
  Divider
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Container from "@mui/material/Container";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";

const RateManagementPage = ({
  title,
  fetchData,
  updateRate,
  entityName,
  entityIdField,
  entityNameField
}) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCustomerCurrency();
  const [entities, setEntities] = useState([]);
  const [hourlyRates, setHourlyRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedStatus, setSavedStatus] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchData();
      setEntities(data);
      const rates = data.reduce((acc, entity) => {
        acc[entity[entityIdField]] = entity.hourly_rate;
        return acc;
      }, {});
      setHourlyRates(rates);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${entityName}s:`, err);
      setError(t("operators.manage.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [fetchData, entityIdField, entityName, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRateChange = (id, rate) => {
    setHourlyRates({ ...hourlyRates, [id]: rate });
    if (savedStatus[id]) {
      setSavedStatus({ ...savedStatus, [id]: false });
    }
  };

  const handleSaveRate = async (id) => {
    try {
      const hourly_rate = parseFloat(hourlyRates[id]);
      if (isNaN(hourly_rate)) return;
      await updateRate(id, hourly_rate);
      setSavedStatus({ ...savedStatus, [id]: true });
      setSnackbarOpen(true);
      setTimeout(() => {
        setSavedStatus((prev) => ({ ...prev, [id]: false }));
      }, 2000);
    } catch (error) {
      console.error("Failed to update internal hourly rate:", error);
      setError(t("operators.manage.errors.updateRateFailed"));
    }
  };

  const handleKeyDown = (event, id) => {
    if (event.key === 'Enter') {
      event.target.blur();
      handleSaveRate(id);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ m: 2 }}>
      {error}
    </Alert>
  );

  return (
    <Container>
      <Box>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {title}
        </Typography>
        {entities.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3, color: 'text.secondary' }}>
            {t("operators.manage.empty")}
          </Typography>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 1 }}>
            <Stack divider={<Divider />}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography fontWeight="bold">{t("operators.manage.table.name")}</Typography>
                <Typography fontWeight="bold">{t("operators.manage.table.internalHourlyRate")}</Typography>
              </Box>
              {entities.map(entity => (
                <Box
                  key={entity[entityIdField]}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { bgcolor: 'action.hover' },
                    padding: 0.5,
                  }}
                >
                  <span>{entity[entityNameField]}</span>
                  <OutlinedInput
                    type="number"
                    value={hourlyRates[entity[entityIdField]]}
                    onChange={(e) => handleRateChange(entity[entityIdField], e.target.value)}
                    onBlur={() => handleSaveRate(entity[entityIdField])}
                    onKeyDown={(e) => handleKeyDown(e, entity[entityIdField])}
                    size="small"
                    inputProps={{ min: 0, step: 0.01, }}
                    startAdornment={<span>{currencySymbol}</span>}
                    endAdornment={savedStatus[entity[entityIdField]] && (
                      <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                    sx={{
                      mb: 0,
                      width: '8rem',
                      '& input': { textAlign: 'right' },
                      '&:hover fieldset': { borderColor: 'primary.main' }
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Card>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          message={t("operators.manage.snackbar.updated")}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </Container>
  );
};

const OperatorsPage = () => {
  const { t } = useTranslation();

  return (
    <RateManagementPage
      title={t("operators.manage.title")}
      fetchData={fetchAllOperators}
      updateRate={updateOperatorHourlyRate}
      entityName="operator"
      entityIdField="operator_id"
      entityNameField="operator_name"
    />
  );
};

export default OperatorsPage;
